import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageThumbnail } from "shared/visuals/image-thumbnail";
import { useDownloadDocument } from "shared/editors/hooks/use-download-document";
import { formatTimestamp } from "utils/formats";
import type { ThumbnailEntry } from "../hooks/use-extra-work-agreement-thumbnails";
import { ImageViewerDialog } from "./imageViewerDialog";

interface Props {
  entry: ThumbnailEntry;
  size?: number | string;
  canDelete: boolean;
  onDelete?: (entry: ThumbnailEntry) => void;
}

const triggerBlobDownload = (blobUrl: string, fileName: string) => {
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  a.click();
};

export const ThumbnailFrame = ({ entry, size = 256, canDelete, onDelete }: Props) => {
  const { t } = useTranslation();
  const downloadDocument = useDownloadDocument();
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const isNumericSize = typeof size === "number";
  const opensViewerOnClick = entry.kind !== "pdf";
  const canDownload = entry.kind !== "pending-upload" || !!entry.pendingFile;

  const handleTileClick = () => {
    if (entry.kind === "pdf") {
      void downloadDocument({ documentId: entry.documentId, name: entry.altText });
      return;
    }
    setIsViewerOpen(true);
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    switch (entry.kind) {
      case "image-with-preview":
      case "image-no-preview":
      case "pdf":
        void downloadDocument({ documentId: entry.documentId, name: entry.altText });
        return;
      case "pending-upload":
        triggerBlobDownload(entry.imageUrl, entry.altText);
        return;
    }
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete?.(entry);
  };

  const formattedDate = entry.uploadedTimestamp
    ? formatTimestamp(entry.uploadedTimestamp, false, "short")
    : "";
  const showActions = canDownload || canDelete;
  const previewImageUrl = getPreviewImageUrl(entry);
  const viewerDocumentId = entry.kind === "image-with-preview" || entry.kind === "image-no-preview" ? entry.documentId : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: size,
        "&:hover .thumbnail-frame-actions": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: isNumericSize ? size : "auto",
          aspectRatio: isNumericSize ? undefined : "1 / 1",
        }}
      >
        {previewImageUrl ? (
          <ImageThumbnail
            imageUrl={previewImageUrl}
            altText={entry.altText}
            size="100%"
            onClick={handleTileClick}
          />
        ) : (
          <PlaceholderTile
            entry={entry}
            onClick={handleTileClick}
            aspect={isNumericSize ? undefined : "1 / 1"}
            fixedSize={isNumericSize ? size : undefined}
          />
        )}
        {showActions && (
          <Stack
            direction="row"
            spacing={0.5}
            className="thumbnail-frame-actions"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              opacity: 0,
              transition: "opacity 0.15s ease-in-out",
            }}
          >
            {canDownload && (
              <Tooltip title={t("dashboard.extraWorkAgreements.create.image.download")}>
                <IconButton
                  size="small"
                  onClick={handleDownloadClick}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.85)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                  }}
                >
                  <FileDownloadOutlinedIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t("common.delete")}>
                <IconButton
                  size="small"
                  onClick={handleDeleteClick}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.85)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                  }}
                >
                  <DeleteOutlinedIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
      <Box sx={{ pt: 0.5, px: 0.25, width: size }}>
        <Typography
          variant="body2"
          color="primary.main"
          title={entry.altText}
          sx={{
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {entry.altText}
        </Typography>
        {formattedDate && (
          <Typography variant="caption" color="grey.50" sx={{ display: "block" }}>
            {formattedDate}
          </Typography>
        )}
      </Box>
      {opensViewerOnClick && (
        <ImageViewerDialog
          isOpen={isViewerOpen}
          onClose={() => setIsViewerOpen(false)}
          imageUrl={getPreviewImageUrl(entry) ?? ""}
          fileName={entry.altText}
          documentId={viewerDocumentId}
        />
      )}
    </Box>
  );
};

const getPreviewImageUrl = (entry: ThumbnailEntry): string | undefined => {
  switch (entry.kind) {
    case "image-with-preview":
    case "pending-upload":
      return entry.imageUrl;
    case "image-no-preview":
    case "pdf":
      return undefined;
  }
};

interface PlaceholderProps {
  entry: ThumbnailEntry;
  onClick: () => void;
  aspect?: string;
  fixedSize?: number;
}

const PlaceholderTile = ({ entry, onClick, aspect, fixedSize }: PlaceholderProps) => {
  const Icon = entry.kind === "pdf" ? PictureAsPdfOutlinedIcon : InsertPhotoOutlinedIcon;
  const iconSize = fixedSize ? fixedSize / 3 : 48;
  return (
    <Box
      onClick={onClick}
      aria-label={entry.altText}
      sx={{
        width: fixedSize ?? "100%",
        height: fixedSize ?? "auto",
        aspectRatio: aspect,
        borderRadius: 1,
        overflow: "hidden",
        border: 1,
        borderColor: "grey.300",
        backgroundColor: "grey.100",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ color: "grey.500", fontSize: iconSize }} />
    </Box>
  );
};
